import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  Typography,
  OutlinedInput,
  useMediaQuery,
  Link as MuiLink,
  Tooltip,
  Switch,
  IconButton
} from '@mui/material';
import ReorderOutlinedIcon from '@mui/icons-material/ReorderOutlined';
import { useTheme } from '@mui/material/styles';
import {
  AssignToSSCCForm,
  DarkUpperSection,
  PageSection,
  PrintShippingLabelsForm,
  SearchField,
  SearchNotFoundCard,
  SearchPromptCard,
  StyledButton,
  StyledSwitch,
  StyledTableHead,
  StyledTableRow
} from '../../components';
import { useEffect, useMemo, useState } from 'react';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAlert, useCurrentUser, useDatastore, useIsOnline } from '../../hooks';
import {
  buildPackage,
  buildSSCC,
  buildReceiptEvent,
  isReceiptStatus,
  OrderLineStatus,
  ReceiptPoStatus,
  receiptStatusRecord,
  SsccStatus
} from '../../common';
import { PrintShippingLabelsModel } from '../../components/modals/PrintShippingLabelsForm';
import MoveToStagingForm, { MoveToStagingModel } from '../../components/modals/MoveToStagingModal';
import { UserDetailsProps } from '../../datastore/userDetails';
import LoadingModal from '../../components/modals/LoadingModal';
import { ReceiptEventSiteProps, SSCCProps, SsccType } from '../../datastore/ssccs';
import { LocationProps, LocationType } from '../../datastore/locations';
import { fetchPODetails } from '../../api/poApi';
import { ReceiptHeader, ReceiptLine } from '../../common/types';
import { createJobInBustle } from '../../common/helperFunctions/bustle';
import { createReceiptCoupa } from '../../common/helperFunctions/coupa';
import dayjs from 'dayjs';
import { PackagePrintProps, SsccPrintProps, SSCCPropsWithTbf } from '../../../../Common/Types/types';
import {
  getDestinationAlias,
  preparePackagesForPrinting,
  printPackages,
  printSscc
} from '../../common/helperFunctions/print';
import OfflineCard from '../../components/cards/OfflineCard';
import { useDisabledPrinterContext } from '../../hooks/useIsDisabledPrinter';
import DisabledPrintAlert from '../../components/alerts/DisabledPrintAlert';
import { yearMonthDateFormat } from '../../common/dateTimes';
import { ReceiptEventPackageHistoryProps } from '../../datastore/receiptEventHistory';
import { ConfirmationDialog } from '../../components/modals/ConfirmDialog';
import { galaxyNoteThreeDimensions } from '../../common/strings';
import {
  TransportBookingEventProps,
  transportBookingLocationAlias,
  transportBookingLocationOneLine,
  unknownTbfField
} from '../../datastore/transportBookingEvent';
import { updateAsnStatus } from '../../common/helperFunctions/sscc';
import { isLocationMatch } from '../../common/helperFunctions/locationConflicts';
import { getProcessAsLargeItemStorage, setProcessAsLargeItemStorage } from '../../common/helperFunctions/localStorage';
import { generateAlphanumericId } from '../../common/helperFunctions/generateId';
import { isValidReceiptStatus } from '../../common/status';
import { sameItemsSummaryRowGenerator, isSubLine, isSummaryLine, thereIsASummaryLine } from './utils';
import { buildCoupaLines, buildPackageLines, coupaReceiptEventGenerator, generateAsn } from './receiptingHelper';

const LOCAL_STORAGE_KEY_SUMMARY_LINE_BG_COLOR = 'LOCAL_STORAGE_KEY_SUMMARY_LINE_BG_COLOR';
const LOCAL_STORAGE_KEY_SHOW_PO_DETAILS = 'LOCAL_STORAGE_KEY_SHOW_PO_DETAILS';

export default function ReceiptPurchaseOrders() {
  const theme = useTheme();
  const store = useDatastore();
  const user = useCurrentUser();
  const alert = useAlert();
  const { isOnline } = useIsOnline();
  const { isDisabled: isAnyPrinterDisabled } = useDisabledPrinterContext();
  const { isDatastoreReady, isSyncing } = store;
  const [processAsLargeItem, setProcessAsLargeItem] = useState(getProcessAsLargeItemStorage(user.cognitoUser.username));
  const isMobile = useMediaQuery(galaxyNoteThreeDimensions);
  const [isSubmitReady, setIsSubmitReady] = useState(false);
  const [searchPO, setSearchPO] = useState('');
  const [isTransportBooking, setIsTransportBooking] = useState(false);
  const [searchASN, setSearchASN] = useState('');
  const [smallItemSsccChoice, setSmallItemSsccChoice] = useState<SSCCProps>();
  const [directReceipt, setDirectReceipt] = useState(false);
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [poData, setPoData] = useState<ReceiptHeader | null>(null);
  const [openPrintLabelsModal, setOpenPrintLabelsModal] = useState(false);
  const [printingData, setPrintingData] = useState<PrintShippingLabelsModel | undefined>();
  const [openAssignSSCCModal, setOpenAssignSSCCModal] = useState(false);
  const [openMoveToStagingModal, setOpenMoveToStagingModal] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetailsProps | null>(null);
  const [poLocation, setPoLocation] = useState<LocationProps | null>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const transportBookingName = 'Transport Booking';

  useEffect(() => {
    setPrintingData(undefined);
    if (isTransportBooking) return;
    setProcessAsLargeItemStorage(processAsLargeItem, user.cognitoUser.username);
  }, [directReceipt, processAsLargeItem]);

  useEffect(() => {
    if (!isTransportBooking) {
      setProcessAsLargeItem(getProcessAsLargeItemStorage(user.cognitoUser.username));
    }
  }, [isTransportBooking]);

  useEffect(() => {
    async function getData() {
      if (searchPO || searchASN) {
        setIsSearchLoading(true);
        const userDetails = await store.userDetails.get(user.cognitoUser.username);
        const poResponse = await fetchPODetails(searchPO, searchASN);
        if (poResponse.error || !poResponse.payload) {
          alert(poResponse.error, 'error');
          setIsSearchLoading(false);
          setPoData(null);
          return;
        }

        if (poResponse.payload?.lines?.length === 0) {
          alert(`There are no items for the given ${searchPO?.length ? 'PO' : 'ASN'}`, 'warning');
          setIsSearchLoading(false);
          setPoData(null);
          return;
        }

        if (poResponse.payload && userDetails && poResponse.payload.site) {
          const location = await store.locations.getNameOrUnknown(poResponse.payload?.site);

          // Check if shipping term is MinRes Air and show notification
          console.log('Shipping term code:', poResponse.payload.shippingTerm?.code);
          if (poResponse.payload?.shippingTerm?.code?.includes('MinRes Air')) {
            // Force a small delay to ensure alert renders after component mount
            setTimeout(() => {
              alert(
                '<strong>Priority Shipment via MinRes Air required.</strong>\n\nLarge Items receipting has been switched on.\n\nFollowing receipt & labelling of goods please place in the MinRes Air designated staging location or cage.',
                'warning',
                {
                  fontSize: '1rem',
                  textAlign: 'center',
                  width: '100%'
                }
              );
            }, 500);

            // Automatically set Process as Large Item to true for MinRes Air shipments
            if (!isTransportBooking) {
              setProcessAsLargeItem(true);
              setDirectReceipt(false);
            }
          }
          // When searching by ASN, the API already returns ASN-specific quantities
          // We should NOT recalculate - just use the quantities as-is from the API
          const updatedLines = poResponse.payload.lines.map((line: ReceiptLine) => {
            return {
              ...line,
              quantityOrdered: Math.max((line.quantityOrdered ?? 0) - (line.quantityReceived ?? 0), 0),
              // Reset quantityReceived to 0 for the receipt form
              quantityReceived: 0,
              partId: line.partId ?? 0,
              sourcePartId: line.sourcePartId ?? ''
            };
          });

          poResponse.payload.lines = updatedLines;

          setPoLocation(location);
          if (poResponse.payload.supplierName === transportBookingName) {
            setIsTransportBooking(true);
            setDirectReceipt(false);
            setProcessAsLargeItem(false);
          } else {
            setIsTransportBooking(false);
          }

          // Check if request type is Long Term Planning (LTP DC)
          const isLtpDc = poResponse.payload.requestType === 'Long Term Planning (LTP DC)';
          const isMinResAir = poResponse.payload?.shippingTerm?.code?.includes('MinRes Air');

          if (isLtpDc) {
            // Only show LTP alert if MinRes Air alert hasn't been shown
            if (!isMinResAir) {
              setTimeout(() => {
                alert(
                  '<strong>Long Term Planning (LTP DC) Consolidation required</strong>\n\nDirect Receipting has been switched on\n\nFollowing receipt & labelling of goods please place in the LTP staging location or Cage for sorting and consolidation.',
                  'info',
                  {
                    fontSize: '1rem',
                    textAlign: 'center',
                    width: '100%'
                  }
                );
              }, 500);
            }

            // Enable Direct Receipt
            setDirectReceipt(true);
            setProcessAsLargeItem(false);
          }

          // Check for P1/P2 priority - only show if neither MinRes Air nor LTP DC
          const isP1OrP2 =
            poResponse.payload.priority?.startsWith('P1') || poResponse.payload.priority?.startsWith('P2');
          if (isP1OrP2 && !isMinResAir && !isLtpDc) {
            setTimeout(() => {
              alert(
                '<strong>This is a P1/P2 Priority Purchase Order.</strong>\n\nOnce receipted follow the guidelines you have been provided for staging & distribution.',
                'warning',
                {
                  fontSize: '1rem',
                  textAlign: 'center',
                  width: '100%'
                }
              );
            }, 500);
          }

          const combinedSameItemsInPoData = sameItemsSummaryRowGenerator(poResponse.payload);

          setPoData(combinedSameItemsInPoData);
          setUserDetails(userDetails);
        } else {
          alert('Could not find Receipt Purchase Order', 'warning');
          setPoData(null);
        }
      } else {
        clearReceiptData();
      }
      setIsSearchLoading(false);
    }

    getData();
  }, [searchPO, searchASN]);

  const isAsnReceipt = useMemo(() => !searchPO && searchASN, [searchASN, searchPO]);

  const colors: { bg: string; font: string }[] = [
    { bg: theme.palette.grey[300], font: theme.palette.text.primary },
    { bg: theme.palette.secondary.main, font: theme.palette.primary.contrastText },
    { bg: theme.palette.secondary.light, font: theme.palette.primary.contrastText }
  ];

  const [summaryLineColor, setSummaryLineColor] = useState<{ bg: string; font: string }>(() => {
    const savedBg = localStorage.getItem(LOCAL_STORAGE_KEY_SUMMARY_LINE_BG_COLOR);

    const defaultColor = colors[0];

    if (!savedBg) {
      return defaultColor;
    }

    return colors.find(({ bg }) => bg === savedBg) ?? defaultColor;
  });

  const [showMoreDetails, setShowMoreDetails] = useState<boolean>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY_SHOW_PO_DETAILS);

    if (saved === 'true') {
      return true;
    }
    if (saved === 'false') {
      return false;
    }

    return true;
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY_SUMMARY_LINE_BG_COLOR, summaryLineColor.bg);
  }, [summaryLineColor.bg]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY_SHOW_PO_DETAILS, `${showMoreDetails}`);
  }, [showMoreDetails]);

  const showPoColumns = (isAsnReceipt && showMoreDetails) || !isAsnReceipt;
  const showAsnColumns = isAsnReceipt;
  const partIdWidth = isAsnReceipt ? (showMoreDetails ? '7%' : '17%') : '17%';
  const typeWidth = showMoreDetails ? '7%' : '10%';
  const descriptionWidth = isAsnReceipt ? (showMoreDetails ? '37%' : '41%') : '41%';

  useEffect(() => {
    if (isSubmitReady && smallItemSsccChoice) {
      onAssignSSCCSubmit();
      setIsSubmitReady(false);
    }
  }, [isSubmitReady, smallItemSsccChoice]);

  function clearReceiptData() {
    setPoData(null);
    setIsTransportBooking(false);
    setPoLocation(null);
    setPrintingData(undefined);
    setSearchASN('');
    setSearchPO('');
    setPrintingData(undefined);
    setOpenAssignSSCCModal(false);
    setSmallItemSsccChoice(undefined);
    setIsReceiptLoading(false);
    setOpenMoveToStagingModal(false);
  }

  function updatedReceiptPurchaseOrder() {
    if (!poData) return;
    const updatedReceiptPurchaseOrder = poData.lines.map((line) => {
      if (line.quantityReceived === line.quantityOrdered) {
        line.quantityReceived = 0;
        line.quantityOrdered = 0;
        line.status = OrderLineStatus.received;
        return line;
      } else if (
        line.quantityReceived &&
        line.quantityReceived > 0 &&
        line.quantityOrdered &&
        line.quantityOrdered > 0
      ) {
        line.quantityOrdered = (line.quantityOrdered ?? 0) - (line.quantityReceived ?? 0);
        line.status = OrderLineStatus.partial;
        return line;
      }
      return line;
    });
    poData.lines = updatedReceiptPurchaseOrder;
    setPoData(poData);
  }

  function getMappedStatus(status: string): string {
    if (isReceiptStatus(status)) {
      return receiptStatusRecord[status];
    } else {
      return 'Unknown';
    }
  }

  async function onPrintLabelsSubmit(data: PrintShippingLabelsModel) {
    const isSmallItemReceipting = !directReceipt && !processAsLargeItem && !isTransportBooking;
    setPrintingData(data);
    if (isSmallItemReceipting) {
      setOpenAssignSSCCModal(true);
    }

    if (processAsLargeItem || isTransportBooking) {
      setOpenMoveToStagingModal(true);
    }

    if (directReceipt) {
      handleDirectReceipting(data);
    }

    setOpenPrintLabelsModal(false);
  }

  async function handlePrinting(packages: PackagePrintProps[], ssccPrintProps?: SsccPrintProps[]) {
    try {
      console.log('🖨️ ===== STARTING PRINT PROCESS =====');
      console.log('📦 Packages to print:', packages);
      console.log('🏷️ SSCC props to print:', ssccPrintProps);

      const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);
      console.log('🖨️ User printer retrieved:', userPrinter);

      if (!userPrinter) {
        throw new Error('No printer set, please set a printer and try again');
      }

      // NEW: Fetch shipping method from PO API for ASN labels
      let shippingMethod = '';
      console.log('🚢 ===== SHIPPING METHOD FETCH PROCESS =====');

      if (packages.length > 0) {
        const firstPackage = packages[0];
        console.log('📦 First package data:', firstPackage);
        console.log('🏷️ PO Number from package:', firstPackage.poNumber);

        if (firstPackage.poNumber) {
          try {
            console.log('🔍 Making PO API call with PO:', firstPackage.poNumber);
            const poResponse = await fetchPODetails(firstPackage.poNumber, '');
            console.log('📋 PO API Response:', {
              error: poResponse.error,
              hasPayload: !!poResponse.payload,
              payload: poResponse.payload
            });

            if (poResponse.payload?.shippingTerm) {
              console.log('🚢 Shipping Term found:', poResponse.payload.shippingTerm);
              console.log('🚢 Shipping Term Code:', poResponse.payload.shippingTerm.code);
              console.log('🚢 Shipping Term Description:', poResponse.payload.shippingTerm.description);

              if (poResponse.payload.shippingTerm.code) {
                shippingMethod = poResponse.payload.shippingTerm.code;
                console.log('✅ Shipping method set to:', shippingMethod);
              } else {
                console.log('⚠️ Shipping term code is empty/null');
              }
            } else {
              console.log('⚠️ No shipping term found in PO response');
            }

            if (poResponse.error) {
              console.log('❌ PO API returned error:', poResponse.error);
            }
          } catch (error) {
            console.error('❌ Exception during PO API call:', error);
            console.log('⚠️ Continuing with empty shipping method rather than failing print');
          }
        } else {
          console.log('⚠️ No PO number found in package data');
        }
      } else {
        console.log('⚠️ No packages provided to handlePrinting function');
      }

      console.log('🚢 Final shipping method value:', shippingMethod);

      // Update packages with shipping method
      console.log('📦 ===== UPDATING PACKAGES WITH SHIPPING METHOD =====');
      const updatedPackages = packages.map((pkg, index) => {
        console.log(`📦 Updating package ${index + 1}:`, {
          originalShippingMethod: pkg.shippingMethod,
          newShippingMethod: shippingMethod,
          asn: pkg.asn,
          poNumber: pkg.poNumber
        });

        return {
          ...pkg,
          shippingMethod: shippingMethod
        };
      });

      console.log('📦 Updated packages with shipping method:', updatedPackages);

      console.log('🖨️ ===== SENDING TO PRINT =====');
      const printPackageResponse = await printPackages(updatedPackages, userPrinter.printerIP, userPrinter.printerPort);
      console.log('📋 Package print response:', printPackageResponse);

      if (printPackageResponse.error) {
        console.error('❌ Package print error:', printPackageResponse.error);
        throw new Error(printPackageResponse.error);
      } else {
        console.log('✅ Package printing successful');
      }

      if (ssccPrintProps) {
        console.log('🏷️ Printing SSCC labels...');
        const printSsccResponse = await printSscc(ssccPrintProps, userPrinter.printerIP, userPrinter.printerPort);
        console.log('📋 SSCC print response:', printSsccResponse);

        if (printSsccResponse.error) {
          console.error('❌ SSCC print error:', printSsccResponse.error);
          throw new Error(printSsccResponse.error);
        } else {
          console.log('✅ SSCC printing successful');
        }
      }

      console.log('🎉 ===== PRINTING COMPLETED SUCCESSFULLY =====');
      alert('Printing completed successfully', 'success');
    } catch (error) {
      console.error('❌ ===== PRINTING PROCESS FAILED =====');
      console.error('❌ Error details:', error);
      alert(`An error occurred when printing: ${error}`, 'error');
    } finally {
      console.log('🏁 ===== PRINT PROCESS FINISHED =====');
      setIsReceiptLoading(false);
    }
  }

  const summaryLineQuantityReceivedQuantitySet = useMemo(() => {
    const map = new Map<number, number>();

    for (const l of poData?.lines || []) {
      if (isSubLine(l)) {
        const id = l.summaryId || 0;
        map.set(id, (map.get(id) ?? 0) + (l.quantityReceived ?? 0));
      }
    }

    return map;
  }, [poData?.lines]);

  //Direct receipting process
  async function handleDirectReceipting(modalData: PrintShippingLabelsModel) {
    try {
      setIsReceiptLoading(true);
      const newSite = buildReceiptEvent({ name: '', site: '' });

      if (!poData) {
        alert("Couldn't retrieve data, please try again.", 'error');
        return;
      }

      const coupaLines = buildCoupaLines(poData.lines);
      const packageLines = buildPackageLines(poData.lines, summaryLineQuantityReceivedQuantitySet);
      const asn = generateAsn();
      const qrCodeId = generateAlphanumericId(17);

      modalData.packages.forEach((pkg) => {
        const newPackage = buildPackage({
          packageType: pkg.packageType,
          po: poData.poNumber,
          asn: asn,
          jobId: poData.jobId,
          assetNumber: poData.assetNumber,
          supplierId: poData.supplierId?.toString() ?? '',
          supplierName: poData.supplierName,
          priority: poData.priority,
          status: ReceiptPoStatus.available,
          packageQuantity: pkg.total,
          dateReceived: printingData?.dateReceived ?? undefined,
          docketNumber: printingData?.docketNumber ?? undefined,
          hasDangerousGoods: pkg.hasDangerousGoods,
          qrCodeId: qrCodeId,
          companyName: poData.company,
          shipToAttention: poData.shipToAttention,
          items: packageLines
        });
        newSite.packages.push(newPackage);
      });

      const latestPackage = newSite.packages[newSite.packages.length - 1];

      if (!latestPackage) {
        alert('Error receipting items into package, please try again.', 'error');
        return;
      }

      const coupaReceiptEvent = coupaReceiptEventGenerator({
        latestPackage,
        coupaLines,
        userName: user.name,
        userLocationName: userDetails?.location?.name || 'UNKNOWN_LOCATION_NAME',
        asn,
        isDirectReceipt: true
      });

      const receiptResponse = await createReceiptCoupa(coupaReceiptEvent);

      if (receiptResponse.error) {
        alert('Error receipting items to coupa, please try again.', 'error');
        console.error(receiptResponse.error);
        return;
      }

      const validPackages = newSite.packages.filter((pkg) => pkg.quantity && pkg.packageType && pkg.asn && pkg.po);
      if (validPackages.length !== newSite.packages.length) {
        alert('There are missing fields in the packages, cant print.', 'error');
        return;
      }

      if (!userDetails?.location || !poLocation) {
        const message = !userDetails?.location
          ? 'You must set a location in `settings` before you can print'
          : 'Issue getting SSCCs destination location, please try again.';
        alert(message, 'error');
        return;
      }
      let printLocation = poLocation;
      if (poLocation.type === LocationType.subLocation) {
        const parentLocation = await store.locations.getById(poLocation.parentLocationId ?? '');
        if (!parentLocation) {
          alert('Could not find SSCCs destination location for printing', 'warning');
          return;
        }
        printLocation = parentLocation;
        printLocation.alias = poLocation.alias;
      }
      const ssccId = 'Direct Receipt';
      const packages = preparePackagesForPrinting(
        validPackages,
        userDetails?.location,
        printLocation,
        ssccId,
        undefined,
        undefined // Remove shipping method from ASN/package labels
      );
      await handlePrinting(packages);
      await createReceiptEventHistory(asn, SsccType.direct, ssccId);
      alert('Receipted Items and Printed', 'success');
      updatedReceiptPurchaseOrder();
    } catch (error) {
      alert('An error occurred in the receipting process, please try again.', 'error');
      console.error('An error occured in the direct receipting process: ', error);
    } finally {
      clearReceiptData();
    }
  }

  //Small item receipting process
  async function onAssignSSCCSubmit() {
    try {
      setIsReceiptLoading(true);
      let site: ReceiptEventSiteProps;
      if (!smallItemSsccChoice) {
        alert('Could not find SSCC', 'warning');
        return;
      }

      if (!poData?.site) {
        alert('Could not find site for given PO', 'warning');
        return;
      }

      if (!poLocation) {
        alert('Could not find location for PO', 'warning');
        return;
      }

      if (!printingData) {
        alert('Could not retrieve printing data', 'warning');
        return;
      }
      let printPkgDestination: LocationProps;
      if (!poLocation) {
        alert('Could not find SSCCs destination location', 'warning');
        return;
      }
      site = buildReceiptEvent({
        name: poLocation.name,
        site: poLocation.alias
      });
      printPkgDestination = poLocation;

      const coupaLines = buildCoupaLines(poData.lines);
      const packageLines = buildPackageLines(poData.lines, summaryLineQuantityReceivedQuantitySet);
      const asn = generateAsn();
      const qrCodeId = generateAlphanumericId(17);

      printingData.packages.forEach((pkg) => {
        const newPackage = buildPackage({
          packageType: pkg.packageType,
          po: poData.poNumber,
          asn: asn,
          jobId: poData.jobId,
          assetNumber: poData.assetNumber,
          supplierId: poData.supplierId?.toString() ?? '',
          priority: poData.priority,
          supplierName: poData.supplierName,
          status: OrderLineStatus.created,
          packageQuantity: pkg.total,
          hasDangerousGoods: pkg.hasDangerousGoods,
          dateReceived: printingData?.dateReceived ?? undefined,
          docketNumber: printingData?.docketNumber ?? undefined,
          qrCodeId: qrCodeId,
          companyName: poData.company,
          shipToAttention: poData.shipToAttention,
          items: packageLines
        });
        site.packages.push(newPackage);
      });

      const latestPackage = site.packages[site.packages.length - 1];

      if (!latestPackage) {
        alert('Error retrieving latest package, please try again.', 'error');
        return;
      }

      const coupaReceiptEvent = coupaReceiptEventGenerator({
        latestPackage,
        coupaLines,
        userName: user.name,
        userLocationName: userDetails?.location?.name || 'UNKNOWN_LOCATION_NAME',
        asn
      });

      const receiptResponse = await createReceiptCoupa(coupaReceiptEvent);
      if (receiptResponse.error) {
        alert('Error receipting items to coupa, please try again.', 'error');
        console.error(receiptResponse.error);
        return;
      }

      smallItemSsccChoice.receiptedEvents.push(site);
      updateAsnStatus(smallItemSsccChoice, SsccStatus.open);
      await store.ssccs.update(smallItemSsccChoice);

      const validPackages = site.packages.filter((pkg) => pkg.quantity && pkg.packageType && pkg.asn && pkg.po);
      if (validPackages.length !== site.packages.length) {
        alert('There are missing fields in the packages, cant print.', 'error');
        return;
      }

      if (!userDetails?.location) {
        alert('You must set a location in `settings` before you can print', 'error');
        return;
      }
      if (printPkgDestination.type === LocationType.subLocation) {
        const parentLocation = await store.locations.getById(printPkgDestination.parentLocationId ?? '');
        if (!parentLocation) {
          alert('Could not find SSCCs destination location for printing', 'warning');
          return;
        }
        printPkgDestination = parentLocation;
        printPkgDestination.alias = poLocation.alias;
      }
      const packages = preparePackagesForPrinting(
        validPackages,
        userDetails?.location,
        printPkgDestination,
        smallItemSsccChoice.id,
        undefined,
        poData?.shippingTerm?.code
      );
      await handlePrinting(packages);
      await createReceiptEventHistory(asn, SsccType.standard, smallItemSsccChoice.id);
      updatedReceiptPurchaseOrder();

      alert('Receipted Items', 'success');
    } catch (error) {
      alert('An error occurred in the receipting process, please try again.', 'error');
      console.error('An error occured in the small item receipting process: ', error);
    } finally {
      clearReceiptData();
    }
  }

  function onAssignSSCCBack() {
    setOpenAssignSSCCModal(false);
    setOpenPrintLabelsModal(true);
  }

  async function handleDestinationMatch(ssccChoice: string | undefined) {
    if (!ssccChoice) {
      alert('Could not find SSCC, please try again.', 'warning');
      return;
    }

    const sscc = await store.ssccs.get(ssccChoice);
    if (!sscc || !sscc.destinationLocationId) {
      alert('Could not find SSCC or its destination location.', 'warning');
      return;
    }

    const ssccDestinationLocation = await store.locations.getById(sscc.destinationLocationId);
    if (!ssccDestinationLocation) {
      alert("Could not find SSCC's destination location.", 'warning');
      return;
    }

    sscc.destinationLocation = ssccDestinationLocation;
    setSmallItemSsccChoice(sscc);

    const isSiteMatch = isLocationMatch(poLocation ?? undefined, ssccDestinationLocation);
    setOpenConfirmDialog(!isSiteMatch);

    if (isSiteMatch) {
      setIsSubmitReady(true);
    }
  }

  async function getTransportBookingEvent() {
    if (!poData?.supplierName) {
      alert('Could not find supplier name for given PO.', 'warning');
      return;
    }

    let transportBookingEvent: TransportBookingEventProps | null = null;

    if (poData.supplierName === transportBookingName) {
      transportBookingEvent = await store.transportBookingEvents.getByPo(Number(poData.poId));
    } else {
      alert('Could not find transport booking for given PO.', 'warning');
    }

    return transportBookingEvent || null;
  }

  //Large item or Transport Booking receipting process
  async function onMoveToStagingSubmit(stagingData: MoveToStagingModel) {
    try {
      setOpenPrintLabelsModal(false);
      setOpenAssignSSCCModal(false);
      setIsReceiptLoading(true);

      if (!poData?.site) {
        alert('Could not find site for given PO.', 'warning');
        return;
      }

      if (!poLocation) {
        alert('Could not find destination location for PO.', 'warning');
        return;
      }

      if (!userDetails?.location) {
        alert('You must set a location in `settings` before you can create an SSCC.', 'warning');
        return;
      }

      if (!printingData) {
        alert('No printing or weight data was saved, please try again.', 'warning');
        return;
      }

      for (const pkg of printingData.packages) {
        if (!pkg.depth || !pkg.height || !pkg.width || !pkg.weight) {
          alert('All packages must have Dimensions and Weight', 'warning');
          return;
        }
      }
      let transportBookingEvent: TransportBookingEventProps | null | undefined = null;
      if (isTransportBooking) {
        transportBookingEvent = await getTransportBookingEvent();
      }
      const destinationAlias = await getDestinationAlias(
        isTransportBooking,
        poLocation,
        store.locations,
        transportBookingEvent?.destinationLocation ?? undefined
      );

      const newSSCC = buildSSCC({
        locationAlias: destinationAlias,
        destinationLocationId: poLocation.id,
        originLocationId: userDetails.location.id,
        stagingLocation: stagingData.location,
        nickname: false,
        isLargeItem: processAsLargeItem,
        isTransportBooking: isTransportBooking,
        transportBookingDestination: transportBookingEvent?.destinationLocation ?? null,
        ssccType: SsccType.large,
        ssccStatus: SsccStatus.staged
      });

      if (!newSSCC) {
        alert('An error has occured whilst creating the SSCC, try again.', 'warning');
        return;
      }

      if (isTransportBooking) {
        newSSCC.bustleServiceType = transportBookingEvent?.serviceType ?? undefined;
      }

      if ((processAsLargeItem || isTransportBooking) && newSSCC && printingData?.packages.length) {
        newSSCC.height = printingData.packages[0].height?.toString() ?? '';
        newSSCC.width = printingData.packages[0].width?.toString() ?? '';
        newSSCC.depth = printingData.packages[0].depth?.toString() ?? '';
        newSSCC.weight = printingData.packages[0].weight?.toString() ?? '';
      }

      const newSite = buildReceiptEvent({ name: poLocation.name, site: poLocation.alias });
      if (!newSSCC) {
        alert('An error has occured whilst creating the SSCC, try again.', 'warning');
        return;
      }

      const coupaLines = buildCoupaLines(poData.lines);
      const packageLines = buildPackageLines(poData.lines, summaryLineQuantityReceivedQuantitySet);

      newSSCC.receiptedEvents.push(newSite);
      const asn = generateAsn();
      printingData.packages.forEach((pkg) => {
        const newPackage = buildPackage({
          packageType: pkg.packageType,
          po: poData.poNumber,
          asn: asn,
          jobId: poData.jobId,
          shipToAttention: poData.shipToAttention,
          assetNumber: poData.assetNumber,
          supplierId: poData.supplierId?.toString() ?? '',
          supplierName: poData.supplierName,
          priority: poData.priority,
          status: OrderLineStatus.created,
          packageQuantity: pkg.total,
          hasDangerousGoods: pkg.hasDangerousGoods,
          dateReceived: printingData?.dateReceived ?? undefined,
          docketNumber: printingData?.docketNumber ?? undefined,
          height: pkg.height?.toString() ?? '',
          width: pkg.width?.toString() ?? '',
          depth: pkg.depth?.toString() ?? '',
          weight: pkg.weight?.toString() ?? '',
          companyName: poData.company,
          qrCodeId: generateAlphanumericId(17),
          items: packageLines
        });
        newSSCC.receiptedEvents[0].packages.push(newPackage);
      });

      const latestPackage = newSite.packages[newSite.packages.length - 1];

      if (!latestPackage) {
        alert('Error receipting items into package, please try again.', 'error');
        return;
      }

      const coupaReceiptEvent = coupaReceiptEventGenerator({
        latestPackage,
        coupaLines,
        userName: user.name,
        userLocationName: userDetails?.location?.name || 'UNKNOWN_LOCATION_NAME',
        asn
      });

      const receiptResponse = await createReceiptCoupa(coupaReceiptEvent);
      if (receiptResponse.error) {
        alert('Error receipting items to coupa, please try again.', 'error');
        console.error(receiptResponse.error);
        return;
      }

      updateAsnStatus(newSSCC, SsccStatus.staged);

      await store.ssccs.create(newSSCC);

      let bustleDestinationLocation = poLocation;
      let parentLocation: LocationProps | undefined = bustleDestinationLocation;
      if (poLocation.type === LocationType.subLocation) {
        parentLocation = await store.locations.getById(poLocation.parentLocationId ?? '');
        if (!parentLocation) {
          alert('Failed to update the SSCC, try again.', 'error');
          return null;
        }
        bustleDestinationLocation.address = parentLocation.address;
        bustleDestinationLocation.suburb = parentLocation.suburb;
        bustleDestinationLocation.state = parentLocation.state;
        bustleDestinationLocation.postcode = parentLocation.postcode;
      }
      if (isTransportBooking && transportBookingEvent) {
        const address =
          transportBookingEvent?.destinationLocation?.addressLine2 === unknownTbfField
            ? transportBookingEvent.destinationLocation.addressLine1
            : `${transportBookingEvent?.destinationLocation?.addressLine1} - ${transportBookingEvent?.destinationLocation?.addressLine2}`;
        bustleDestinationLocation.address = address;
        bustleDestinationLocation.name = transportBookingEvent?.destinationLocation?.businessName || unknownTbfField;
        bustleDestinationLocation.suburb = transportBookingEvent?.destinationLocation?.suburb;
        bustleDestinationLocation.state = transportBookingEvent?.destinationLocation?.state;
        bustleDestinationLocation.alias = transportBookingLocationAlias(
          transportBookingEvent?.destinationLocation || null
        );
        bustleDestinationLocation.postcode = transportBookingEvent?.destinationLocation?.postCode.toString();
      }

      const ssccWithExtraTbfFields: SSCCPropsWithTbf = {
        ...newSSCC,
        transportBookingEvent: {
          additionalField1: transportBookingEvent?.additionalField1 ?? '',
          additionalField2: transportBookingEvent?.additionalField2 ?? '',
          additionalField3: transportBookingEvent?.additionalField3 ?? '',
          notesContent: transportBookingEvent?.notesContent ?? '',
          destinationLocation: transportBookingEvent?.destinationLocation ?? null,
          originLocation: transportBookingEvent?.originLocation ?? null
        }
      };

      const response = await createJobInBustle(
        ssccWithExtraTbfFields,
        userDetails.location,
        bustleDestinationLocation,
        parentLocation?.deliveryDays
      );

      if (response.error) {
        throw new Error(response.error);
      }

      const currentDate = dayjs();
      const validPackages = newSite.packages.filter((pkg) => pkg.quantity && pkg.packageType && pkg.asn && pkg.po);
      if (validPackages.length !== newSite.packages.length) {
        alert('There are missing fields in the packages, cant print.', 'error');
        return;
      }

      const sourceAddress = userDetails.location.toOneLineAddress();

      let destinationAddress = poLocation.toOneLineAddress();

      let printPkgDestination = poLocation;
      if (printPkgDestination.type === LocationType.subLocation) {
        const parentLocation = await store.locations.getById(printPkgDestination.parentLocationId ?? '');
        if (!parentLocation) {
          alert('Could not find SSCCs destination location for printing', 'warning');
          return;
        }
        destinationAddress = parentLocation.toOneLineAddress();
      }

      const packages = preparePackagesForPrinting(
        validPackages,
        userDetails?.location,
        printPkgDestination,
        newSSCC.id,
        isTransportBooking ? transportBookingEvent?.destinationLocation : null
      );

      const ssccPrints: SsccPrintProps[] = validPackages.flatMap((pkg, i) => {
        return Array.from({ length: pkg.quantity || 0 }, (_, index) => ({
          createdAt: currentDate.format(yearMonthDateFormat),
          packageNumber: '0',
          totalPackages: '0',
          packageIndex: index.toString(),
          sourceAddress: sourceAddress,
          destinationAddress: isTransportBooking
            ? transportBookingLocationOneLine(transportBookingEvent?.destinationLocation || null)
            : destinationAddress,
          destinationAlias: destinationAlias,
          containerType: printingData.packages[i].packageType ?? '',
          length: printingData.packages[i].depth?.toString() ?? '',
          width: printingData.packages[i].width?.toString() ?? '',
          height: printingData.packages[i].height?.toString() ?? '',
          weight: printingData.packages[i].weight?.toString() ?? '',
          qrCodeId: pkg.qrCodeId,
          ssccId: newSSCC.id,
          nickname: newSSCC.nickname || '',
          asn: asn,
          hasDangerousGoods: printingData.packages[i].hasDangerousGoods ?? false,
          shippingMethod: poData?.shippingTerm?.code || ''
        }));
      });

      ssccPrints.forEach((ssccPackage, index) => {
        ssccPackage.packageNumber = (index + 1).toString();
        ssccPackage.totalPackages = ssccPrints.length.toString();
      });

      await handlePrinting(packages, ssccPrints);
      await createReceiptEventHistory(asn, isTransportBooking ? SsccType.transportBooking : SsccType.large, newSSCC.id);

      alert('Receipted items, created SSCC and printed', 'success');
      updatedReceiptPurchaseOrder();
    } catch (error) {
      alert('An error occurred in the receipting process, please try again.', 'error');
      console.error('An error occured in the large item receipting process: ', error);
    } finally {
      clearReceiptData();
    }
  }

  function onMoveToStagingBack() {
    setOpenPrintLabelsModal(true);
    setOpenMoveToStagingModal(false);
  }

  function onLineQuantityChange(lineId: number | null | undefined, quantity: string) {
    const clone = structuredClone(poData);
    if (clone) {
      const line = clone.lines.find((line) => line.id === lineId);

      if (line && line.quantityOrdered) {
        line.quantityReceived =
          Number(quantity) > line.quantityOrdered ? Number(line.quantityOrdered) : Number(quantity);
        setPoData(clone);
      }
    }
  }

  function onAutoFill() {
    const clone = structuredClone(poData);
    if (clone) {
      for (const line of clone.lines) {
        if (isSummaryLine(line)) {
          continue;
        }

        line.quantityReceived = line.quantityOrdered;
      }
      setPoData(clone);
    }
  }

  async function createReceiptEventHistory(internalAsn: string, receiptType: string, associatedSSCC: string) {
    try {
      const receiptEventPackageHistory: ReceiptEventPackageHistoryProps = {
        id: crypto.randomUUID(),
        asn: internalAsn,
        receiptType: receiptType,
        associatedSSCC: associatedSSCC,
        receiptedBy: user.email,
        actionTime: dayjs()
      };

      await store.receiptEventPackageHistories.create(receiptEventPackageHistory);
    } catch (error) {
      console.error('Error creating receipt event history: ', error);
    }
  }

  const renderSwitches = () => (
    <>
      <StyledSwitch
        value={processAsLargeItem}
        disabled={isTransportBooking}
        onChange={() => {
          setProcessAsLargeItem(!processAsLargeItem);
          setDirectReceipt(false);
        }}
        label="Process as Large Item"
      />
      <StyledSwitch
        value={directReceipt}
        disabled={isTransportBooking}
        onChange={() => {
          setProcessAsLargeItem(false);
          setDirectReceipt(!directReceipt);
        }}
        label="Direct Receipt"
      />
    </>
  );
  const hasSearched = searchPO || searchASN;

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
            Receipt Purchase Orders
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <SearchField
              placeholder="Purchase Order"
              startIcon={<QrCodeIcon />}
              includeEndIcon
              onTrigger={(value) => {
                setSearchASN('');
                setSearchPO(value);
                setIsTransportBooking(false);
              }}
              value={searchPO}
            />
            <SearchField
              placeholder="Docket Number"
              startIcon={<QrCodeIcon />}
              includeEndIcon
              onTrigger={(value) => {
                setSearchPO('');
                setSearchASN(value);
                setIsTransportBooking(false);
              }}
              value={searchASN}
            />
            {!isMobile && !isTransportBooking && <>{renderSwitches()}</>}
            <Stack direction="row" sx={{ flex: 1, justifyContent: 'end' }}>
              <StyledButton
                variant="contained"
                onClick={() => setOpenPrintLabelsModal(true)}
                disabled={
                  user.isReadOnly ||
                  isAnyPrinterDisabled ||
                  !isDatastoreReady ||
                  poData?.lines.every((line) => line.quantityReceived === 0) ||
                  !poData?.lines.length
                }
                sx={{
                  whiteSpace: isMobile ? 'nowrap' : null
                }}
                title={!isDatastoreReady ? 'Please wait for data sync to complete' : undefined}>
                Next: Shipping Labels
              </StyledButton>
            </Stack>
          </Stack>
          {isMobile && !isTransportBooking && (
            <Stack direction="row" spacing="3em">
              {renderSwitches()}
            </Stack>
          )}
        </Stack>
      </DarkUpperSection>

      <PageSection>
        {poData ? (
          <Stack spacing={3} sx={{ marginTop: '1em', marginBottom: '3em' }}>
            {isAnyPrinterDisabled ? (
              <DisabledPrintAlert />
            ) : directReceipt ? (
              <Alert severity="warning">
                <b>Important: </b>Direct receipting is turned on. This feature should be used only when items are
                directly shipped to the destination.
              </Alert>
            ) : (
              <></>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4,
                padding: 4,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '0', md: '1px' },
                  height: { xs: '0', md: 'calc(100% - 64px)' },
                  backgroundColor: theme.palette.divider
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 'calc(100% - 64px)', md: '0' },
                  height: { xs: '1px', md: '0' },
                  backgroundColor: theme.palette.divider
                }
              }}>
              {/* Location & Delivery Section */}
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="primary" letterSpacing={0.2}>
                    Location & Delivery
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                      fontWeight={500}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block'
                      }}>
                      Site
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {poData?.site}
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Ship To
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {poData?.shipToAttention}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Company
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {poData?.company}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              {/* Order Details Section */}
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <DescriptionIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="primary" letterSpacing={0.2}>
                    Order Details
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                      fontWeight={500}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block'
                      }}>
                      PO Number
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {poData?.poNumber}
                      </Typography>
                      {poData?.poNumber && (
                        <MuiLink
                          component="a"
                          href={`${process.env.REACT_APP_COUPA_BASE_URL}${poData.poNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}>
                          View
                        </MuiLink>
                      )}
                    </Stack>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Status
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {getMappedStatus(poData?.poStatus ?? '')}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Priority
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={
                          poData?.priority?.startsWith('P1') || poData?.priority?.startsWith('P2') ? 600 : 400
                        }
                        sx={{
                          mt: 0.5,
                          color:
                            poData?.priority?.startsWith('P1') || poData?.priority?.startsWith('P2')
                              ? 'warning.main'
                              : 'text.primary'
                        }}>
                        {poData?.priority || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              {/* Supplier Section */}
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <BusinessIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="primary" letterSpacing={0.2}>
                    Supplier
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                      fontWeight={500}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block'
                      }}>
                      Name
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {poData?.supplierName}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Processing Section */}
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <LocalShippingIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="primary" letterSpacing={0.2}>
                    Processing
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Request Type
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {poData?.requestType || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color={theme.palette.text.secondary}
                        fontWeight={500}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block'
                        }}>
                        Shipping Method
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {poData?.shippingTerm?.code || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}>
              <Box
                sx={{
                  padding: 3,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900'
                }}>
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body1" fontWeight={600}>
                      Consignment Items{' '}
                      <Box component="span" sx={{ color: 'primary.main' }}>
                        ({poData.lines.filter((line) => !isSummaryLine(line)).length} lines)
                      </Box>
                    </Typography>
                    {isAsnReceipt ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">Show PO details</Typography>
                        <Switch
                          size="small"
                          checked={showMoreDetails}
                          onChange={(e) => setShowMoreDetails(e.target.checked)}
                          inputProps={{ 'aria-label': 'More details toggle' }}
                        />
                      </Box>
                    ) : (
                      <></>
                    )}
                    {isAsnReceipt && thereIsASummaryLine(poData.lines || []) ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">Color</Typography>
                        {colors.map((c) => (
                          <IconButton
                            key={c.bg + c.font}
                            onClick={() => setSummaryLineColor(c)}
                            sx={{
                              bgcolor: c.bg,
                              '&:hover': { bgcolor: c.bg },
                              border: summaryLineColor.bg === c.bg ? '2px solid black' : 'none'
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <></>
                    )}
                  </Stack>
                  <StyledButton
                    variant="contained"
                    onClick={onAutoFill}
                    sx={{ padding: '0.6em 1.5em', fontSize: '0.875rem' }}
                    disabled={poData.lines
                      .filter((line) => !isSummaryLine(line))
                      .some((line: ReceiptLine) => !isValidReceiptStatus(line.status))}>
                    Autofill Quantities
                  </StyledButton>
                </Stack>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  '& .MuiTableCell-root:first-of-type': { pl: 4 },
                  '& .MuiTableCell-root:last-of-type': { pr: 4 }
                }}>
                <Table>
                  <StyledTableHead>
                    {showPoColumns ? <TableCell width="7%">{isAsnReceipt ? '# PO Line' : '#'}</TableCell> : <></>}
                    {showPoColumns ? (
                      <TableCell width="10%">{isAsnReceipt ? 'PO Line Status' : 'Status'}</TableCell>
                    ) : (
                      <></>
                    )}
                    {showAsnColumns ? <TableCell width="7%">{showMoreDetails ? '# ASN Line' : '#'}</TableCell> : <></>}
                    {showAsnColumns ? <TableCell width="10%">ASN Line Status</TableCell> : <></>}
                    <TableCell width={partIdWidth}>Part ID</TableCell>
                    <TableCell width={typeWidth} title="Purchase Type">
                      Type
                    </TableCell>
                    <TableCell width={descriptionWidth}>Description</TableCell>
                    <TableCell width="15%">Quantity</TableCell>
                  </StyledTableHead>
                  <TableBody>
                    {poData.lines.map((line) => {
                      const isDisabled = !isValidReceiptStatus(line.status) || !line.quantityOrdered;
                      const fontColour = isDisabled ? theme.palette.text.secondary : theme.palette.text.primary;
                      const isSummaryRow = isSummaryLine(line);
                      const summaryBgColor = summaryLineColor.bg;
                      const summaryLineFontColour = summaryLineColor.font;

                      if (isSummaryRow) {
                        return (
                          <StyledTableRow
                            key={line.id}
                            sx={{
                              backgroundColor: summaryBgColor,
                              borderTop: `2px solid ${summaryBgColor}`,
                              borderLeft: `2px solid ${summaryBgColor}`,
                              borderRight: `2px solid ${summaryBgColor}`,
                              '&:hover': {
                                backgroundColor: summaryBgColor
                              }
                            }}>
                            {isAsnReceipt && showMoreDetails ? (
                              <TableCell
                                sx={{
                                  color: summaryLineFontColour,
                                  fontWeight: isDisabled || isAsnReceipt ? 'normal' : 'bold'
                                }}>
                                {line.poLineNum}
                              </TableCell>
                            ) : (
                              <></>
                            )}
                            {isAsnReceipt && showMoreDetails ? (
                              <TableCell sx={{ color: summaryLineFontColour }}>
                                {getMappedStatus(line.status ?? '')}
                              </TableCell>
                            ) : (
                              <></>
                            )}
                            <TableCell
                              colSpan={isAsnReceipt ? (showMoreDetails ? 5 : 5) : 3}
                              sx={{ py: 1.5, textAlign: 'center', verticalAlign: 'middle' }}>
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
                                <Typography
                                  sx={{ color: summaryLineFontColour, letterSpacing: '0.03em', fontSize: '0.9rem' }}
                                  fontStyle="italic">
                                  <b>Same Item:</b> {isAsnReceipt ? 'ASN ' : ''}Lines{' '}
                                  {line.summaryLineSubLineNumberRange} will be consolidated into a single receipt.
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="baseline">
                                <Typography sx={{ color: summaryLineFontColour }} variant="subtitle1" fontWeight={600}>
                                  {summaryLineQuantityReceivedQuantitySet.get(line.summaryId || 0) ?? 0}
                                </Typography>
                                <Typography sx={{ color: summaryLineFontColour }}>
                                  of {line.quantityOrdered ?? 0}
                                </Typography>
                              </Stack>
                            </TableCell>
                          </StyledTableRow>
                        );
                      }

                      // --- Regular row ---
                      return (
                        <StyledTableRow
                          key={line.id}
                          sx={{
                            ...(isSubLine(line)
                              ? {
                                  borderLeft: `2px solid ${summaryBgColor}`
                                }
                              : {}),
                            ...(isSubLine(line)
                              ? {
                                  borderRight: `2px solid ${summaryBgColor}`
                                }
                              : {}),
                            ...(isSubLine(line) && line.lastSubLineOfSummary
                              ? {
                                  borderBottom: `2px solid ${summaryBgColor}`
                                }
                              : {}),
                            ...(isDisabled
                              ? {
                                  '&:hover': {
                                    backgroundColor: theme.palette.background.default
                                  }
                                }
                              : {})
                          }}>
                          {(isAsnReceipt && showMoreDetails) || !isAsnReceipt ? (
                            <TableCell
                              sx={{ color: fontColour, fontWeight: isDisabled || isAsnReceipt ? 'normal' : 'bold' }}>
                              {isSubLine(line) ? '' : line.poLineNum}
                            </TableCell>
                          ) : (
                            <></>
                          )}
                          {(isAsnReceipt && showMoreDetails) || !isAsnReceipt ? (
                            <TableCell sx={{ color: fontColour }}>
                              {isSubLine(line) ? '' : getMappedStatus(line.status ?? '')}
                            </TableCell>
                          ) : (
                            <></>
                          )}
                          {isAsnReceipt ? (
                            <TableCell sx={{ color: fontColour, fontWeight: isDisabled ? 'normal' : 'bold' }}>
                              {line.asnLineNum}
                            </TableCell>
                          ) : (
                            <></>
                          )}
                          {isAsnReceipt ? (
                            <TableCell sx={{ color: fontColour }}>
                              {getMappedStatus(line.asnLineStatus ?? '')}
                            </TableCell>
                          ) : (
                            <></>
                          )}

                          <TableCell sx={{ color: fontColour }}>{line.sourcePartId}</TableCell>
                          <TableCell sx={{ color: fontColour }}>{line.purchaseType}</TableCell>
                          <TableCell sx={{ color: fontColour }}>{line.description}</TableCell>
                          <TableCell sx={{ color: fontColour }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                              {isDisabled ? (
                                <Tooltip
                                  enterTouchDelay={0}
                                  title={
                                    !line.quantityOrdered
                                      ? 'Available quantity for receiving is 0.'
                                      : `Can't receipt line in an invalid status. PO line status: ${getMappedStatus(line.status ?? '')}`
                                  }>
                                  <OutlinedInput
                                    sx={{ flex: 1 }}
                                    inputProps={{
                                      style: {
                                        padding: '0.5em'
                                      },
                                      min: 0,
                                      max: line.quantityOrdered,
                                      step: 1
                                    }}
                                    type="number"
                                    value={line.quantityReceived}
                                    onChange={(event) => onLineQuantityChange(line.id, event.target.value)}
                                    disabled={isDisabled}
                                  />
                                </Tooltip>
                              ) : (
                                <OutlinedInput
                                  sx={{ flex: 1 }}
                                  inputProps={{
                                    style: {
                                      padding: '0.5em'
                                    },
                                    min: 0,
                                    max: line.quantityOrdered,
                                    step: 1
                                  }}
                                  type="number"
                                  value={line.quantityReceived}
                                  onChange={(event) => onLineQuantityChange(line.id, event.target.value)}
                                />
                              )}
                              <Typography sx={{ flex: 1 }}>of {line.quantityOrdered}</Typography>
                            </Stack>
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Stack>
        ) : (
          <></>
        )}
        <>
          {!poData && !hasSearched && (
            <Stack justifyContent="center">
              <SearchPromptCard
                title="Start Receiving Items"
                description={
                  isOnline
                    ? 'Search by Purchase Order or Docket Number to begin the receipt process.'
                    : 'Search for a Purchase Order or Docket Number to begin the receipt process.'
                }
                searchPrompt="Enter a PO number or ASN/Docket number above"
              />
            </Stack>
          )}

          {!poData && hasSearched && !isSearchLoading && (
            <Stack justifyContent="center">
              <SearchNotFoundCard />
            </Stack>
          )}
        </>
      </PageSection>

      {openPrintLabelsModal && (
        <PrintShippingLabelsForm
          open={openPrintLabelsModal}
          data={printingData}
          asn={searchASN ? searchASN : ''}
          isTransportBooking={isTransportBooking}
          isLargeItem={processAsLargeItem}
          onSubmit={onPrintLabelsSubmit}
          onClose={() => setOpenPrintLabelsModal(false)}
          isDirectReceipt={directReceipt}
        />
      )}
      {openAssignSSCCModal && (
        <AssignToSSCCForm
          open={openAssignSSCCModal}
          onSubmit={handleDestinationMatch}
          onBack={onAssignSSCCBack}
          onClose={() => setOpenAssignSSCCModal(false)}
          isLargeItem={processAsLargeItem}
          poLocation={poLocation || undefined}
        />
      )}

      {openMoveToStagingModal && (
        <MoveToStagingForm
          open={openMoveToStagingModal}
          onSubmit={onMoveToStagingSubmit}
          onBack={onMoveToStagingBack}
          onClose={() => setOpenMoveToStagingModal(false)}
        />
      )}
      <LoadingModal
        isLoading={isReceiptLoading || isSearchLoading}
        titleText={isReceiptLoading ? 'Receipting' : 'Searching'}
        bodyText={isReceiptLoading ? 'Please wait, receipting items' : 'Please wait, searching items'}
      />
      <ConfirmationDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setSmallItemSsccChoice(undefined);
        }}
        onConfirm={() => {
          setOpenConfirmDialog(false);
          setIsSubmitReady(true);
        }}
        title="Location Conflict"
        content="The receipted items destination site does not match the SCCC destination. Are you sure wish to continue?"
      />
    </>
  );
}
